import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Link } from "react-router-dom";

const AZURE_FUNC_URL = process.env.REACT_APP_AIQ_FUNC_URL;
const CAPTCHA_SECRET_KEY=process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY;

export const Demo = () => {
 
    const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    number: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

const handleSubmit = async (e) => {
    e.preventDefault();
 
    // 🚫 block if captcha not verified
    if (!captchaToken) {
      setError("Please verify the captcha");
      return;
    }
 
    setIsSubmitting(true);
    setError("");
 
    try {
      await axios.post(AZURE_FUNC_URL, {
        name: formData.name,
        email: formData.email,
        number: formData.number,
        company: formData.company,
        message: formData.message,
      });
 
      setIsSubmitted(true);
 
      // ✅ reset captcha after submit
      recaptchaRef.current.reset();
      setCaptchaToken(null);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to submit request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  if (isSubmitted) {
    return (
      <div
        className="pt-16 md:pt-20 flex items-center"
        data-testid="demo-success"
      >
        <div className="container-width">
          <motion.div
            className="max-w-xl mx-auto text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-16 h-16 bg-neutral-950 rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h1
              className="text-3xl md:text-4xl font-bold text-neutral-950 mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Request Received
            </h1>
            <p className="text-neutral-600 mb-8">
              Thank you for your interest in Bridge Format AIQ. Our team will
              contact you within 24 hours to schedule your enterprise demo.
            </p>
            <p className="text-sm text-neutral-500">
              In the meantime, feel free to explore our{" "}
              <Link to="/#features" className="text-neutral-950 underline">
                features
              </Link>{" "}
              and{" "}
              <Link to="/who-we-serve" className="text-neutral-950 underline">
                use cases
              </Link>
              .
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20" data-testid="demo-page">
      {/* Hero Section */}
      <section
        className="section-padding hero-texture min-h-[70vh] flex items-center"
        data-testid="demo-hero"
      >
        <div className="container-width relative z-10">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-neutral-950 mb-8"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Get in Touch with Bridge Format AIQ
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-neutral-600">
              Whether you need a demo, have a question, or want to explore use
              cases, we’re here to help you get the most out of Bridge Format
              AIQ.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section
        className="section-padding bg-neutral-50"
        data-testid="demo-form-section"
      >
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                data-testid="demo-form"
              >
                {error && (
                  <div
                    className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm text-sm"
                    data-testid="form-error"
                  >
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="h-12 rounded-sm border-neutral-200 focus:ring-1 focus:ring-neutral-950 focus:border-neutral-950"
                      placeholder="John Smith"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Work Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="h-12 rounded-sm border-neutral-200 focus:ring-1 focus:ring-neutral-950 focus:border-neutral-950"
                      placeholder="john@company.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company">Company *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="h-12 rounded-sm border-neutral-200 focus:ring-1 focus:ring-neutral-950 focus:border-neutral-950"
                      placeholder="Acme Corporation"
                      data-testid="input-company"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="number">Mobile *</Label>
                    <Input
                      id="number"
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                      required
                      className="h-12 rounded-sm border-neutral-200 focus:ring-1 focus:ring-neutral-950 focus:border-neutral-950"
                      placeholder="Phone Number"
                      data-testid="input-number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">
                    Additional Information (Optional)
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="rounded-sm border-neutral-200 focus:ring-1 focus:ring-neutral-950 focus:border-neutral-950"
                    placeholder="Tell us about your specific document challenges..."
                    data-testid="input-message"
                  />
                </div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={CAPTCHA_SECRET_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                />
                <button
                  type="submit"
                  disabled={isSubmitting || !captchaToken}
                  className="btn-primary w-full gap-2 btn-hover-scale disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-demo-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Schedule Enterprise Demo
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-neutral-500 text-center">
                  By submitting this form, you agree to our privacy policy. We
                  will only use your information to contact you about Bridge
                  Format AIQ.
                </p>
              </form>
            </motion.div>

            {/* Info Panel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="bg-secondary text-white p-12">
                <p className="text-xs font-mono uppercase tracking-wider text-white/80 mb-6">
                  What to Expect
                </p>
                <h3
                  className="text-2xl font-semibold mb-6"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Your Demo Will Include:
                </h3>
                <ul className="space-y-4">
                  {[
                    "Live document transformation with your real files",
                    "Format governance configuration walkthrough",
                    "Integration architecture review",
                    "Custom pricing based on your volume",
                    "Q&A with our solutions team",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-white/80 mt-1 flex-shrink-0" />
                      <span className="text-white/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border border-neutral-200 p-8">
                <p className="text-xs font-mono uppercase tracking-wider text-neutral-500 mb-4">
                  Typical Response Time
                </p>
                <div
                  className="text-4xl font-bold text-neutral-950 mb-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  &lt; 24 Hours
                </div>
                <p className="text-neutral-600">
                  Our team will reach out within one business day to schedule
                  your personalized demo.
                </p>
              </div>

              <div className="border border-neutral-200 p-8">
                <p className="text-xs font-mono uppercase tracking-wider text-neutral-500 mb-4">
                  Need Immediate Help?
                </p>
                <p className="text-neutral-600 mb-4">
                  For urgent inquiries, contact our enterprise team directly:
                </p>
                <a
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=talk@bridgeformataiq.com"
                  target="_blank"
                  className="font-semibold text-neutral-950"
                >
                  talk@bridgeformataiq.com
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Demo;
